import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Trophy,
  Medal,
  Award,
  TrendingUp,
  TrendingDown,
  Users,
  Target,
  DollarSign,
  BarChart3,
  ArrowUpDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface PerformanceData {
  seller: string;
  budgets: number;
  value: number;
  conversion: number;
  rank: number;
}

interface PerformanceTableProps {
  data: PerformanceData[];
  loading?: boolean;
}

type SortField = 'rank' | 'seller' | 'budgets' | 'value' | 'conversion';
type SortOrder = 'asc' | 'desc';

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div className="flex items-center gap-1">
        <Trophy className="h-4 w-4 text-yellow-500" />
        <span className="text-sm font-bold text-yellow-600">1º</span>
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="flex items-center gap-1">
        <Medal className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-bold text-foreground">2º</span>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="flex items-center gap-1">
        <Award className="h-4 w-4 text-orange-600" />
        <span className="text-sm font-bold text-orange-700">3º</span>
      </div>
    );
  }
  return (
    <Badge variant="outline" className="text-xs">
      {rank}º
    </Badge>
  );
}

function ConversionBadge({ rate }: { rate: number }) {
  const getVariant = (conversionRate: number) => {
    if (conversionRate >= 70) return 'default';
    if (conversionRate >= 50) return 'secondary';
    return 'destructive';
  };

  const getTextClass = (conversionRate: number) => {
    if (conversionRate >= 70) return 'text-green-700 dark:text-green-300';
    if (conversionRate >= 50) return 'text-amber-700 dark:text-amber-300';
    return 'text-red-700 dark:text-red-300';
  };

  const getBgClass = (conversionRate: number) => {
    if (conversionRate >= 70) return 'bg-green-500/10 border-green-500/20';
    if (conversionRate >= 50) return 'bg-amber-500/10 border-amber-500/20';
    return 'bg-red-500/10 border-red-500/20';
  };

  return (
    <Badge
      variant={getVariant(rate)}
      className={cn('text-xs', getBgClass(rate), getTextClass(rate))}
    >
      {rate.toFixed(1)}%
    </Badge>
  );
}

function TrendIndicator({ current, previous }: { current: number; previous: number }) {
  const trend = ((current - previous) / previous) * 100;
  const isPositive = trend >= 0;

  if (Math.abs(trend) < 0.1) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  return (
    <div className={cn('flex items-center gap-1 text-sm', isPositive ? 'text-finance-green' : 'text-finance-red')}>
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      <span>{isPositive ? '+' : ''}{trend.toFixed(1)}%</span>
    </div>
  );
}

export function PerformanceTable({ data, loading = false }: PerformanceTableProps) {
  const [sortField, setSortField] = useState<SortField>('rank');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!data) return [];

    return [...data].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'rank':
          comparison = a.rank - b.rank;
          break;
        case 'seller':
          comparison = a.seller.localeCompare(b.seller);
          break;
        case 'budgets':
          comparison = a.budgets - b.budgets;
          break;
        case 'value':
          comparison = a.value - b.value;
          break;
        case 'conversion':
          comparison = a.conversion - b.conversion;
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, sortField, sortOrder]);

  // Calculate averages and totals
  const totalBudgets = data.reduce((sum, item) => sum + item.budgets, 0);
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  const averageConversion = data.length > 0 ? data.reduce((sum, item) => sum + item.conversion, 0) / data.length : 0;

  // Get top performers
  const topPerformer = data.find(item => item.rank === 1);
  const topConversion = data.reduce((max, item) => item.conversion > max.conversion ? item : max, data[0] || { conversion: 0, seller: '' });

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-10 w-24" />
              </div>
              <Skeleton className="h-96 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Performance Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Orçamentos</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalBudgets}</div>
            <p className="text-xs text-muted-foreground">
              Entre todos os vendedores
            </p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
            <p className="text-xs text-muted-foreground">
              Valor somado de todos os orçamentos
            </p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa Média</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageConversion.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              Taxa de conversão média
            </p>
          </CardContent>
        </Card>

        <Card className="card-elevated border-amber-500/20 bg-amber-500/10">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Melhor Desempenho</CardTitle>
            <Trophy className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold text-amber-700 dark:text-amber-200">{topPerformer?.seller || '-'}</div>
            <p className="text-xs text-muted-foreground">
              {topPerformer?.value ? formatCurrency(topPerformer.value) : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Performance Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Ranking de Performance
              </CardTitle>
              <CardDescription>
                Desempenho individual dos vendedores
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={`${sortField}-${sortOrder}`}
                onValueChange={(value) => {
                  const [field, order] = value.split('-') as [SortField, SortOrder];
                  setSortField(field);
                  setSortOrder(order);
                }}
              >
                <SelectTrigger className="w-40">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rank-asc">Ranking</SelectItem>
                  <SelectItem value="seller-asc">Nome (A-Z)</SelectItem>
                  <SelectItem value="value-desc">Valor (Maior)</SelectItem>
                  <SelectItem value="budgets-desc">Orçamentos (Maior)</SelectItem>
                  <SelectItem value="conversion-desc">Conversão (Maior)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>Vendedor</TableHead>
                  <TableHead className="text-right cursor-pointer" onClick={() => handleSort('budgets')}>
                    <div className="flex items-center justify-end gap-1">
                      Orçamentos
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right cursor-pointer" onClick={() => handleSort('value')}>
                    <div className="flex items-center justify-end gap-1">
                      Valor Total
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right cursor-pointer" onClick={() => handleSort('conversion')}>
                    <div className="flex items-center justify-end gap-1">
                      Taxa de Conversão
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((item) => (
                  <TableRow key={item.seller} className={item.rank <= 3 ? 'bg-muted/30' : ''}>
                    <TableCell>
                      <RankBadge rank={item.rank} />
                    </TableCell>
                    <TableCell className="font-medium">{item.seller}</TableCell>
                    <TableCell className="text-right">
                      <div className="text-right">
                        <div className="font-medium">{item.budgets}</div>
                        <div className="text-sm text-muted-foreground">
                          Ticket: {formatCurrency(item.value / (item.budgets || 1))}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-medium">{formatCurrency(item.value)}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <ConversionBadge rate={item.conversion} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Performance Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Trophy className="h-4 w-4 text-yellow-600" />
              Campeão de Vendas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{topPerformer?.seller || '-'}</div>
            <div className="text-sm text-muted-foreground">
              {topPerformer?.value ? formatCurrency(topPerformer.value) : '-'} em {topPerformer?.budgets || 0} orçamentos
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-finance-green" />
              Mestre da Conversão
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{topConversion.seller}</div>
            <div className="text-sm text-muted-foreground">
              {topConversion.conversion.toFixed(1)}% de taxa de conversão
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              Média do Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{averageConversion.toFixed(1)}%</div>
            <div className="text-sm text-muted-foreground">
              Taxa de conversão média entre todos
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}